import {
    Button,
    Divider,
    IconButton,
    Stack,
    Typography,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent
} from "@mui/material";
import React, {useEffect, useState} from "react";
import EntityTable, {EntityTableHeaderProps} from "../../../components/list/EntityList";
import {BoatInRace} from "../../../schemas/boats/boatInRaceSchema";
import {useRace} from "../../../utils/races/RaceContext";
import useSWR from "swr";
import {deleteFetcher, getFetcher, postFetcher, putFetcher} from "../../../utils/api/fetchers";
import Loading from "../../../components/status/Loading";
import {useCategories} from "../../../utils/categories/CategoriesContext";
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import buildUrl from "../../../utils/api/buildUrl";
import useTableParams from "../../../components/list/useTableParams";
import {useConfirm} from "material-ui-confirm";
import {useNotification} from "../../../utils/hooks/useNotification";
import useMediaQuery from "@mui/material/useMediaQuery";
import {FinishLineRecord} from "../../../schemas/boats/boatFinishLineRecordSchema";
import {DropResult} from "@hello-pangea/dnd";
import {getCategoryColor} from "../../../utils/categories/categoryColors";

const FinishLineRecording: React.FC = () => {
    const confirm = useConfirm();
    const {showNotification} = useNotification();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("lg"));
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//-------------------------------------------------------------------------------------------------------------------------------
    const [unregisteredBoatDialogOpen, setUnregisteredBoatDialogOpen] = useState(false);
    const [orderedRecords, setOrderedRecords] = useState<FinishLineRecord[]>([]);
//-------------------------------------------------------------------------------------------------------------------------------
    const {race, isLoading: isLoadingRace} = useRace();
    const {categories, isLoading: isLoadingCategories} = useCategories();
//-------------------------------------------------------------------------------------------------------------------------------
    const finishedTableId = "finished";
    const {
        filters: fFinished,
    } = useTableParams(finishedTableId);

    let urlFinishedBoats = buildUrl(`/races/${race?.id}/finish`, {
        filters: fFinished,
    });
    const {
        data: finishLineRecords,
        mutate: mutateFinishLineRecords,
    } = useSWR<FinishLineRecord[]>(race ? urlFinishedBoats : null, getFetcher, {keepPreviousData: true});
//-------------------------------------------------------------------------------------------------------------------------------
    const urlAllFinishLineRecords = `/races/${race?.id}/finish`;
    const {
        data: allFinishLineRecords,
        mutate: mutateAllFinishLineRecords,
    } = useSWR<FinishLineRecord[]>(race ? urlAllFinishLineRecords : null, getFetcher, {keepPreviousData: true});
//-------------------------------------------------------------------------------------------------------------------------------
    const registeredTableId = "registered";
    const {
        sortKeys: skRegistered,
        sortDirections: sdRegistered,
        setSort: setSortRegistered,
        filters: fRegistered,
        clearFilters: clearFiltersRegistered,
    } = useTableParams(registeredTableId);

    let urlRegisteredBoats = buildUrl(`/races/${race?.id}/boats`, {
        sortBy: skRegistered,
        sortDirections: sdRegistered,
        filters: fRegistered
    });
    if (allFinishLineRecords && allFinishLineRecords.length > 0) {
        const completedBoatIds = allFinishLineRecords.filter(finishLineRecord => {
            return race?.rules.categoryLapsCount[finishLineRecord.boat.category.id] === finishLineRecord.leap;
        });
        urlRegisteredBoats += `&filter:id=!${completedBoatIds.map(boat => boat.boat.id).join(",")}`;
    }
    const {
        data: registeredBoatsFiltered,
    } = useSWR<BoatInRace[]>(race ? urlRegisteredBoats : null, getFetcher, {keepPreviousData: true});
//-------------------------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        setSortRegistered("category", "asc");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (finishLineRecords) setOrderedRecords(finishLineRecords);
    }, [finishLineRecords]);

    if (isLoadingRace || !race || isLoadingCategories || !categories) return <Loading/>

    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination) return;
        const fromIndex = result.source.index;
        const toIndex = result.destination.index;
        if (fromIndex === toIndex) return;

        const updated = Array.from(orderedRecords);
        const [moved] = updated.splice(fromIndex, 1);
        updated.splice(toIndex, 0, moved);
        setOrderedRecords(updated);

        try {
            await putFetcher(`/races/${race.id}/finish/${moved.id}/move`, {
                place: toIndex + 1,
            });
            showNotification("Pořadí bylo změněno", "success");
            mutateFinishLineRecords();
            mutateAllFinishLineRecords();
        } catch (e) {
            showNotification("Chyba při změně pořadí", "error");
            setOrderedRecords(finishLineRecords ?? []);
            console.error(e);
        }
    };

    const handleAddRecord = async (boatId: number) => {
        postFetcher(`/races/${race.id}/finish`, {boatId})
            .then(() => {
                showNotification("Průjezd lodě byl úspěšně evidován", "success");
                clearFiltersRegistered();
                mutateFinishLineRecords();
                mutateAllFinishLineRecords();
            })
            .catch((error) => {
                if (error.response?.status === 400) {
                    showNotification("Loď má již evidovány vše průjezdy", "error");
                    return;
                }
                showNotification("Chyba při evidování průjezdu lodě", "error");
                console.error(error);
            });
    }

    const handleDeleteRecord = async (recordId: number) => {
        confirm({
            description: "Opravdu chcete zrušit tento záznam průjezdu?",
            title: "Zrušení průjezdu lodi",
            confirmationText: "Potvrdit",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        }).then(() => {
            deleteFetcher(`/races/${race.id}/finish/${recordId}`)
                .then(() => {
                    showNotification("Záznam průjezdu byl úspěšně zrušen", "success");
                    mutateFinishLineRecords();
                    mutateAllFinishLineRecords();
                })
                .catch((error) => {
                    showNotification("Chyba při zrušení průjezdu", "error");
                    console.error(error);
                })
        }).catch(() => {
        });
    }

    const registeredTable = <EntityTable
        tableId={registeredTableId}
        headers={[
            {
                key: "controls", label: "Kolo", render: (boat: BoatInRace) => {
                    const category = categories.find(c => c.categoryNumber === boat.category.categoryNumber);
                    const lastFinishLineRecordOfBoat = finishLineRecords?.filter(f =>
                        f.boat.boatId === boat.boatId).sort((a, b) => b.leap - a.leap)[0];
                    if (!category) return null;
                    if (lastFinishLineRecordOfBoat) {
                        return `${isMobile ? "Kolo: " : ""}${lastFinishLineRecordOfBoat.leap}/${race?.rules.categoryLapsCount[category.id]}`;
                    } else {
                        return `${isMobile ? "Kolo: " : ""}0/${race?.rules.categoryLapsCount[category.id]}`;
                    }
                }
            },
            {key: 'name', label: 'Jméno', sortable: true, filtered: true},
            {
                key: 'category',
                label: 'Kategorie',
                sortable: true,
                filtered: true,
                filterConfig: {
                    type: 'multiSelect',
                    options: [
                        ...(categories).map(category => ({
                            label: category.name,
                            value: category.categoryNumber.toString()
                        }))
                    ]
                },
                render: (category: any) => (
                    <span
                        style={{color: getCategoryColor(category?.categoryNumber), fontWeight: "bold"}}
                    >
                                        {category?.name}
                    </span>
                )
            },
            race.rules.startNumbers ? {
                key: 'startNumber',
                label: 'Startovní číslo',
                sortable: true,
                filtered: true,
                filterConfig: {
                    type: 'number',
                    min: 0
                },
                render: (type: number) => type ||
                    <Typography color="textSecondary" fontStyle="italic">Neuvedeno</Typography>
            } : {
                key: "boatNumber",
                label: "Evidenční číslo",
                sortable: true,
                filtered: true,
                filterConfig: {
                    type: 'number',
                    min: 0
                },
                render: (type: string) => type ||
                    <Typography color="textSecondary" fontStyle="italic">Neuvedeno</Typography>
            },
            {
                key: 'controls',
                render:
                    (boat: BoatInRace) => (
                        <IconButton color="success" title="Označit průjezd"
                                    onClick={() => handleAddRecord(boat.boatId)}>
                            <SportsScoreIcon/>
                        </IconButton>
                    )
            }
        ] as EntityTableHeaderProps[]}
        entities={registeredBoatsFiltered ?? []}
        paged={false}
        indexes
        entitiesCount={registeredBoatsFiltered?.length ?? 0}
        showLoading={false}
    />;

    return (
        <>
            <Stack direction="row" divider={!isSmall ? <Divider orientation="vertical" flexItem/> : undefined}
                   spacing={2} justifyContent="space-between" overflow="auto">
                <Stack direction="column" spacing={0} flexGrow={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h5">
                            Evidované průjezdy cílem
                        </Typography>
                        {isSmall && (
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => setUnregisteredBoatDialogOpen(true)}
                                title="Evidovat další průjezdy lodí"
                            >
                                Evidovat průjezdy
                            </Button>
                        )}
                    </Stack>
                    <EntityTable
                        tableId={finishedTableId}
                        headers={[
                            {key: 'place', label: '#'},
                            {key: 'boat.name', label: 'Jméno', filtered: true},
                            {
                                key: 'boat.category',
                                label: 'Kategorie',
                                filtered: true,
                                filterConfig: {
                                    type: 'multiSelect',
                                    options: [
                                        ...(categories).map(category => ({
                                            label: category.name,
                                            value: category.categoryNumber.toString()
                                        }))
                                    ]
                                },
                                render: (category: any) => (
                                    <span
                                        style={{color: getCategoryColor(category?.categoryNumber), fontWeight: "bold"}}
                                    >
                                        {category?.name}
                                    </span>
                                )
                            },
                            race.rules.startNumbers ? {
                                key: 'boat.startNumber',
                                label: 'Startovní číslo',
                                filtered: true,
                                filterConfig: {
                                    type: 'number',
                                    min: 0
                                },
                                render: (type: number) => type ||
                                    <Typography color="textSecondary" fontStyle="italic">Neuvedeno</Typography>
                            } : {
                                key: "boat.boatNumber",
                                label: "Evidenční číslo",
                                filtered: true,
                                filterConfig: {
                                    type: 'number',
                                    min: 0
                                },
                                render: (type: string) => type ||
                                    <Typography color="textSecondary" fontStyle="italic">Neuvedeno</Typography>
                            },
                            {
                                key: 'leap',
                                label: 'Kolo',
                                render: (leap: number) => {
                                    return `${leap}.`;
                                }
                            },
                            {
                                key: 'controls',
                                render:
                                    (finishLineRecord: FinishLineRecord) => (
                                        <IconButton color="error" title="Zrušit záznam průjezdu"
                                                    onClick={() => handleDeleteRecord(finishLineRecord.id)}>
                                            <HighlightOffIcon/>
                                        </IconButton>
                                    )
                            }
                        ] as EntityTableHeaderProps[]}
                        entities={orderedRecords ?? []}
                        onDragEnd={handleDragEnd}
                        draggable
                        paged={false}
                        entitiesCount={finishLineRecords?.length ?? 0}
                        showLoading={false}
                    />
                </Stack>
                {!isSmall && (
                    <Stack direction="column" spacing={0} flexGrow={1}>
                        <Typography variant="h5">
                            Aktuálně závodící lodě
                        </Typography>
                        {registeredTable}
                    </Stack>
                )}
            </Stack>
            <Dialog
                open={unregisteredBoatDialogOpen}
                onClose={() => setUnregisteredBoatDialogOpen(false)}
                fullScreen={isSmall}
                maxWidth="lg"
            >
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography variant="h5" fontWeight="bold">Evidování průjezdů</Typography>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => setUnregisteredBoatDialogOpen(false)}
                            startIcon={<HighlightOffIcon/>}
                        >
                            Zavřít
                        </Button>
                    </Stack>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                        <Typography variant="h5">
                            Aktuálně závodící lodě
                        </Typography>
                    </Stack>
                    <Stack direction="column" spacing={0} flexGrow={1}>
                        {registeredTable}
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default FinishLineRecording;
